import type { RegistryCurrencyCodesFragment } from '@graphql/fragments/currency.graphql.generated';
import { useSetRegistryCurrencyMutation } from '@graphql/mutations/currency.graphql.generated';
import { useGetCurrencyCodesQuery } from '@graphql/queries/currency.graphql.generated';
import { useGetPayoutAccountInfoQuery } from '@graphql/queries/registry.graphql.generated';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { useRegistryCurrencyCodes } from '@shared/utils/currency/useRegistryCurrencyCodes';
import { hasActiveStripeAccount } from '@shared/utils/payoutAccount';
import { useCallback, useEffect, useState } from 'react';

type CurrencyDialogControllerArgs = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  eventId: Maybe<string>;
  registryCurrency: Maybe<RegistryCurrencyCodesFragment>;
  setRegistryCurrency: Maybe<(c: RegistryCurrencyCodesFragment) => void>;
}>;

export const useCurrencyDialogController = ({ isOpen, onClose, eventId, registryCurrency: currentCurrency, setRegistryCurrency }: CurrencyDialogControllerArgs) => {
  const [setCurrency] = useSetRegistryCurrencyMutation();

  const { data: payoutData } = useGetPayoutAccountInfoQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    skip: !isOpen || !eventId,
    variables: { eventId: eventId! },
  });

  const payoutAccount = payoutData?.getRegistryEventData?.payoutAccount;
  const isCurrencyLocked = !!payoutAccount?.status && hasActiveStripeAccount(payoutAccount.status);
  const lockedCurrencyCode = isCurrencyLocked ? (payoutAccount?.currencyCode ?? currentCurrency?.code ?? DEFAULT_CURRENCY) : null;

  const currencies = useRegistryCurrencyCodes();

  // Keep reference to the full currency list for finding fragments on save
  const { data: currencyCodeData } = useGetCurrencyCodesQuery();
  const currencyItems = currencyCodeData?.getRegistryStaticData?.currencies;

  const initialCode = (currentCurrency?.code ?? DEFAULT_CURRENCY) as CurrencyCodeType;
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<CurrencyCodeType | ''>(initialCode);

  useEffect(() => {
    setSelectedCurrencyCode((currentCurrency?.code ?? DEFAULT_CURRENCY) as CurrencyCodeType);
  }, [currentCurrency]);

  const handleOnSave = useCallback(
    (eventId: string, currencyCode: string) => {
      setCurrency({
        variables: {
          eventId,
          payload: { code: currencyCode },
        },
        refetchQueries: () => ['GetAdminRegistryData', 'PublicShopRoot'],
      });

      // Find the full fragment to notify parent (preserves existing API contract)
      if (setRegistryCurrency && currencyItems) {
        const allItems = [...(currencyItems.topRanked ?? []), ...(currencyItems.ordered ?? [])];
        const fragment = allItems.find(item => item.code === currencyCode);
        if (fragment) {
          setRegistryCurrency(fragment);
        }
      }
    },
    [setCurrency, setRegistryCurrency, currencyItems],
  );

  // When locked, force the selector to show the payout account's currency
  useEffect(() => {
    if (isCurrencyLocked && lockedCurrencyCode) {
      setSelectedCurrencyCode(lockedCurrencyCode as CurrencyCodeType);
    }
  }, [isCurrencyLocked, lockedCurrencyCode]);

  const onCancel = () => {
    setSelectedCurrencyCode(initialCode);
    onClose();
  };

  const onSave = useCallback(() => {
    if (isCurrencyLocked) {
      onClose();
      return;
    }
    if (eventId && selectedCurrencyCode) {
      handleOnSave(eventId, selectedCurrencyCode);
    }
    onClose();
  }, [handleOnSave, onClose, eventId, selectedCurrencyCode, isCurrencyLocked]);

  return { onCancel, onSave, currencies, selectedCurrencyCode, setSelectedCurrencyCode, isCurrencyLocked, lockedCurrencyCode };
};
